<template>
  <main class="py-6">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="md:flex md:items-start md:justify-between mb-6">
        <div class="min-w-0 flex-1">
          <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            {{ pageTitle }}
          </h2>
          <p class="mt-1 text-sm text-gray-600">
            {{ pageDescription }}
          </p>
        </div>
        <div class="mt-4 md:mt-0 md:ml-4 flex gap-3">
          <router-link
            to="/blocks"
            class="kairo-button kairo-button-gray"
          >
            Back to blocks
          </router-link>
          <button
            @click="saveBlock"
            type="button"
            :disabled="isSaving || isLoading"
            class="kairo-button kairo-button-green"
          >
            <svg v-if="isSaving" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            {{ saveButtonLabel }}
          </button>
        </div>
      </div>

      <div class="grid gap-6 lg:grid-cols-2">
        <section class="bg-white shadow rounded-xl border border-gray-200 overflow-hidden">
          <div class="border-b border-gray-200 px-6 py-5">
            <h3 class="text-base font-semibold text-gray-900">Configuration</h3>
          </div>

          <div class="divide-y divide-gray-200">
            <div class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)] md:items-center">
              <label for="block_type" class="text-sm font-medium text-gray-900">Type</label>
              <select
                id="block_type"
                v-model="draft.type"
                @change="handleTypeChange"
                :disabled="isTypeLocked"
                class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                :class="isTypeLocked ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''"
              >
                <option v-for="option in blockTypes" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>

            <div class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)] md:items-center">
              <label for="block_title" class="text-sm font-medium text-gray-900">Title</label>
              <input
                id="block_title"
                v-model="draft.title"
                type="text"
                class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Block title"
              >
            </div>

            <div v-if="isTimeBlock" class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)] md:items-center">
              <label for="block_display_mode" class="text-sm font-medium text-gray-900">Display mode</label>
              <div>
                <select
                  id="block_display_mode"
                  v-model="draft.displayMode"
                  class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="compact">Compact</option>
                  <option value="default">Default</option>
                </select>
                <p class="mt-2 text-sm text-gray-500">Used only in BlockBuilder and the Blocks page. The board layout still controls its own compact/default mode.</p>
              </div>
            </div>

            <div v-if="showsDurationFields" class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)] md:items-center">
              <div class="text-sm font-medium text-gray-900">Duration</div>
              <div class="grid grid-cols-2 gap-3">
                <select
                  v-model.number="draftSizeHours"
                  class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option v-for="option in sizeOptionsHours" :key="'h-' + option.value" :value="option.value">{{ option.text }} hours</option>
                </select>
                <select
                  v-model.number="draftSizeMinutes"
                  class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option v-for="option in sizeOptionsMinutes" :key="'m-' + option.value" :value="option.value">{{ option.text }} minutes</option>
                </select>
              </div>
            </div>

            <div v-if="isCountdown" class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)]">
              <div class="text-sm font-medium text-gray-900 md:pt-2">Countdown</div>
              <div class="grid gap-3 sm:grid-cols-2">
                <div>
                  <label for="countdown_minutes" class="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Minutes</label>
                  <input
                    id="countdown_minutes"
                    v-model.number="countdownSizeMinutes"
                    type="number"
                    min="0"
                    step="1"
                    class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                </div>
                <div>
                  <label for="countdown_seconds" class="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Seconds</label>
                  <select
                    id="countdown_seconds"
                    v-model.number="countdownSizeSeconds"
                    class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option v-for="option in sizeOptionsSeconds" :key="'s-' + option.value" :value="option.value">{{ option.text }} seconds</option>
                  </select>
                </div>
              </div>
            </div>

            <div v-if="isProgress" class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)] md:items-center">
              <label for="progress_start" class="text-sm font-medium text-gray-900">Start</label>
              <input
                id="progress_start"
                v-model="progressStartInput"
                type="datetime-local"
                class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
            </div>

            <div v-if="isProgress" class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)] md:items-center">
              <label for="progress_end" class="text-sm font-medium text-gray-900">End</label>
              <input
                id="progress_end"
                v-model="progressEndInput"
                type="datetime-local"
                class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
            </div>

            <div v-if="showsIncreaseValue" class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)] md:items-center">
              <label for="increase_value" class="text-sm font-medium text-gray-900">Increase by</label>
              <input
                id="increase_value"
                v-model.number="draft.increaseValue"
                type="number"
                min="1"
                step="1"
                class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
            </div>

            <div v-if="isCounter" class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)] md:items-center">
              <label for="counter_goal" class="text-sm font-medium text-gray-900">Goal</label>
              <input
                id="counter_goal"
                v-model.number="draft.size"
                type="number"
                min="0"
                step="1"
                class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
            </div>

            <div v-if="isCounter" class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)]">
              <div class="text-sm font-medium text-gray-900 md:pt-2">Counters</div>
              <div class="relative overflow-visible rounded-lg border border-gray-200 shadow-sm">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"></th>
                      <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Title</th>
                      <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Increase by</th>
                      <th class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Action</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200 bg-white">
                    <tr v-for="(counter, index) in draft.counters" :key="counter.id">
                      <td class="relative w-16 overflow-visible px-4 py-4">
                        <div class="h-6 w-6 cursor-pointer" @click="counterChangeIconOpen(index)">
                          <BlockIcon :type="counter.icon" :isPreview="true" :color="draft.color" />
                        </div>
                        <div v-if="iconEdited === index" class="absolute left-0 top-full z-50 mt-2 rounded-md border border-gray-200 bg-white p-2 shadow-lg" style="width: min(32rem, calc(100vw - 2rem));">
                          <BlockIconPicker
                            :type="counter.icon"
                            :position="index"
                            @blockIconUpdate="counterChangeIcon"
                          />
                        </div>
                      </td>
                      <td class="px-4 py-4">
                        <input
                          v-model="counter.title"
                          type="text"
                          class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                      </td>
                      <td class="px-4 py-4">
                        <input
                          v-model.number="counter.increaseValue"
                          type="number"
                          min="0"
                          step="1"
                          class="block w-28 border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                      </td>
                      <td class="px-4 py-4 text-right">
                        <button type="button" @click="counterDelete(index)" class="kairo-button kairo-button-yellow kairo-button-small">
                          Delete
                        </button>
                      </td>
                    </tr>
                    <tr>
                      <td class="relative w-16 overflow-visible px-4 py-4">
                        <div class="h-6 w-6 cursor-pointer" @click="counterChangeIconOpen('new')">
                          <BlockIcon :type="counterNew.icon" :isPreview="true" :color="draft.color" />
                        </div>
                        <div v-if="iconEdited === 'new'" class="absolute left-0 top-full z-50 mt-2 rounded-md border border-gray-200 bg-white p-2 shadow-lg" style="width: min(32rem, calc(100vw - 2rem));">
                          <BlockIconPicker
                            :type="counterNew.icon"
                            position="new"
                            @blockIconUpdate="counterChangeIcon"
                          />
                        </div>
                      </td>
                      <td class="px-4 py-4">
                        <input
                          v-model="counterNew.title"
                          type="text"
                          placeholder="Counter title"
                          class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                      </td>
                      <td class="px-4 py-4">
                        <input
                          v-model.number="counterNew.increaseValue"
                          type="number"
                          min="0"
                          step="1"
                          class="block w-28 border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                      </td>
                      <td class="px-4 py-4 text-right">
                        <button type="button" @click="counterAdd" class="kairo-button kairo-button-green kairo-button-small">
                          Add
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="isProgress" class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)]">
              <div class="text-sm font-medium text-gray-900 md:pt-2">Counters</div>
              <div class="relative overflow-visible rounded-lg border border-gray-200 shadow-sm">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"></th>
                      <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Title</th>
                      <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                      <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Value</th>
                      <th class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Action</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200 bg-white">
                    <tr v-for="(counter, index) in draft.counters" :key="counter.id || index">
                      <td class="relative w-16 overflow-visible px-4 py-4">
                        <div class="h-6 w-6 cursor-pointer" @click="progressChangeIconOpen(index)">
                          <BlockIcon :type="counter.icon" :isPreview="true" :color="draft.color" />
                        </div>
                        <div v-if="progressIconEdited === index" class="absolute left-0 top-full z-50 mt-2 rounded-md border border-gray-200 bg-white p-2 shadow-lg" style="width: min(32rem, calc(100vw - 2rem));">
                          <BlockIconPicker
                            :type="counter.icon"
                            :position="index"
                            @blockIconUpdate="progressChangeIcon"
                          />
                        </div>
                      </td>
                      <td class="px-4 py-4">
                        <input
                          v-model="counter.title"
                          type="text"
                          class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                      </td>
                      <td class="px-4 py-4">
                        <select
                          v-model="counter.type"
                          class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option v-for="counterType in progressCounterTypes" :key="counterType.value" :value="counterType.value">{{ counterType.text }}</option>
                        </select>
                      </td>
                      <td class="px-4 py-4">
                        <input
                          v-model="counter.day"
                          type="text"
                          class="block w-28 border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                      </td>
                      <td class="px-4 py-4 text-right">
                        <button type="button" @click="progressDelete(index)" class="kairo-button kairo-button-yellow kairo-button-small">
                          Delete
                        </button>
                      </td>
                    </tr>
                    <tr>
                      <td class="relative w-16 overflow-visible px-4 py-4">
                        <div class="h-6 w-6 cursor-pointer" @click="progressChangeIconOpen('new')">
                          <BlockIcon :type="progressCounterNew.icon" :isPreview="true" :color="draft.color" />
                        </div>
                        <div v-if="progressIconEdited === 'new'" class="absolute left-0 top-full z-50 mt-2 rounded-md border border-gray-200 bg-white p-2 shadow-lg" style="width: min(32rem, calc(100vw - 2rem));">
                          <BlockIconPicker
                            :type="progressCounterNew.icon"
                            position="new"
                            @blockIconUpdate="progressChangeIcon"
                          />
                        </div>
                      </td>
                      <td class="px-4 py-4">
                        <input
                          v-model="progressCounterNew.title"
                          type="text"
                          placeholder="Counter title"
                          class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                      </td>
                      <td class="px-4 py-4">
                        <select
                          v-model="progressCounterNew.type"
                          class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option v-for="counterType in progressCounterTypes" :key="counterType.value" :value="counterType.value">{{ counterType.text }}</option>
                        </select>
                      </td>
                      <td class="px-4 py-4">
                        <input
                          v-model="progressCounterNew.day"
                          type="text"
                          class="block w-28 border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                      </td>
                      <td class="px-4 py-4 text-right">
                        <button type="button" @click="progressAdd" class="kairo-button kairo-button-green kairo-button-small">
                          Add
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="isLink" class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)]">
              <div class="text-sm font-medium text-gray-900 md:pt-2">Link</div>
              <div class="grid gap-3">
                <input
                  v-model="draft.link"
                  type="url"
                  class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="https://example.com"
                >
                <input
                  v-model="draft.imageLink"
                  type="url"
                  class="block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Optional image URL"
                >
              </div>
            </div>

            <div class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)]">
              <div class="text-sm font-medium text-gray-900 md:pt-2">Color</div>
              <div class="flex flex-wrap gap-3">
                <button
                  v-for="colorOption in colors"
                  :key="colorOption.value"
                  type="button"
                  @click="draft.color = colorOption.value"
                  class="h-10 w-10 rounded-full border-2 shadow-sm flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2"
                  :class="[
                    'bg-' + colorOption.value + '-400',
                    draft.color === colorOption.value ? 'border-gray-900' : 'border-white',
                    'focus:ring-' + colorOption.value + '-500'
                  ]"
                >
                  <svg v-if="draft.color === colorOption.value" class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </button>
              </div>
            </div>

            <div v-if="showsFacePicker" class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)]">
              <div class="text-sm font-medium text-gray-900 md:pt-2">Face</div>
              <div v-if="isTimeBlock" class="sm:col-span-1">
                <div
                  v-for="faceOption in faces"
                  :key="faceOption.value"
                  @click="updateFace(faceOption.value)"
                  class="relative inline-block align-top rounded-full h-12 w-12 m-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 shadow border-2"
                  :class="[
                    'bg-' + draft.color + '-400 hover:bg-' + draft.color + '-700 focus:ring-' + draft.color + '-500',
                    draft.faceType === faceOption.value ? 'border-gray-900' : 'border-white'
                  ]"
                >
                  <BlockFace
                    class="h-full w-full"
                    :type="faceOption.value"
                    :color="draft.color"
                    :skin="draft.skin"
                    :isActive="true"
                    :isPreview="true"
                  />
                  <span v-if="draft.faceType === faceOption.value" class="absolute -bottom-1 -right-1 inline-flex items-center justify-center h-5 w-5 rounded-full bg-white text-gray-900 shadow pointer-events-none">
                    <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>
                  </span>
                </div>
              </div>
              <BlockIconPicker
                v-else
                :type="draft.faceType"
                @blockIconUpdate="updateFace"
              />
            </div>

            <div v-if="showsSkinPicker" class="px-6 py-5 grid gap-4 md:grid-cols-[160px_minmax(0,1fr)]">
              <div class="text-sm font-medium text-gray-900 md:pt-2">Skin tone</div>
              <div>
                <div
                  v-for="skinOption in skins"
                  :key="skinOption.value"
                  @click="draft.skin = skinOption.value"
                  class="relative inline-flex align-top rounded-full h-10 w-10 m-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 shadow border-2"
                  :class="draft.skin === skinOption.value ? 'border-gray-900' : 'border-white'"
                  :style="'background-color: #' + skinOption.colors[50]"
                >
                  <span v-if="draft.skin === skinOption.value" class="absolute inset-0 inline-flex items-center justify-center text-gray-900">
                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>
                  </span>
                </div>
              </div>
            </div>

          </div>
        </section>

        <aside class="lg:sticky lg:top-20 self-start">
          <div class="bg-white shadow rounded-xl border border-gray-200 overflow-hidden">
            <div class="border-b border-gray-200 px-6 py-5">
              <h3 class="text-base font-semibold text-gray-900">Live preview</h3>
              <p class="mt-1 text-sm text-gray-600">Uses the same block renderer as the board.</p>
            </div>
            <div class="p-6 bg-gray-50">
              <div class="mx-auto max-w-sm pointer-events-none">
                <AutoFitPreview :padding="12" :min-scale="0.45" :max-scale="1">
                  <component :is="previewComponent" v-bind="previewProps" />
                </AutoFitPreview>
              </div>
            </div>
          </div>

          <div class="mt-6 bg-white shadow rounded-xl border border-gray-200 overflow-hidden">
            <div class="border-b border-gray-200 px-6 py-5">
              <h3 class="text-base font-semibold text-gray-900">Embed</h3>
              <p class="mt-1 text-sm text-gray-600">Configure how this block can be shared outside Kairo.</p>
            </div>

            <div v-if="!currentUser" class="m-4 rounded-md shadow-md bg-yellow-50 p-4">
              <div class="flex gap-3">
                <div class="flex-shrink-0">
                  <svg class="border-2 border-yellow-200 h-12 w-12 text-yellow-400 bg-yellow-100 p-2 rounded" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div class="min-w-0 flex-grow">
                  <h3 class="text-md font-medium text-yellow-800">Embed links are only available for signed-in users.</h3>
                  <p class="mt-1 text-sm text-yellow-700">Sign up to create embeddable blocks and manage their options.</p>
                </div>
                <div class="flex-shrink-0">
                  <router-link
                    to="/signup"
                    class="mt-2 inline-flex items-center px-3 py-2 border-2 border-yellow-200 text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >Start trial</router-link>
                </div>
              </div>
            </div>

            <div class="px-6 py-5 space-y-5">
              <div class="flex items-center justify-between gap-4">
                <div>
                  <label for="builder_embed" class="text-sm font-medium text-gray-900">Enable embed</label>
                  <p class="mt-1 text-sm text-gray-500">Create a shareable embed URL for this block.</p>
                </div>
                <div class="flex text-xs">
                  <button
                    id="builder_embed"
                    @click="embedToggle"
                    type="button"
                    :disabled="!currentUser"
                    class="mt-1 bg-gray-200 relative inline-flex flex-shrink-0 h-5 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                    :class="{'bg-indigo-600': !!draft.embedId}"
                    role="switch"
                    :aria-checked="!!draft.embedId"
                  >
                    <span class="sr-only">Toggle embed</span>
                    <span class="translate-x-0 pointer-events-none relative inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                          :class="{'translate-x-4': !!draft.embedId}">
                      <span class="opacity-0 ease-out duration-100 absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                            aria-hidden="true"
                            :class="{'opacity-100 ease-in duration-200': !draft.embedId}">
                        <svg class="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 12 12">
                          <path d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                      </span>
                      <span class="opacity-0 ease-out duration-100 absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                            :class="{'opacity-100 ease-in duration-200': !!draft.embedId}"
                            aria-hidden="true">
                        <svg class="h-3 w-3 text-indigo-600" fill="currentColor" viewBox="0 0 12 12">
                          <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                        </svg>
                      </span>
                    </span>
                  </button>
                </div>
              </div>

              <div v-if="draft.embedId" class="space-y-4 border-t border-gray-100 pt-5">
                <div>
                  <label for="builder_embed_url" class="block text-sm font-medium text-gray-900">Embed URL</label>
                  <p v-if="!canCopyEmbedUrl" class="mt-2 text-sm font-medium text-green-600">
                    Save the block to get the embed URL.
                  </p>
                  <div v-else class="mt-2 flex rounded-md shadow-sm relative">
                    <input
                      id="builder_embed_url"
                      :value="embedURL"
                      disabled
                      type="text"
                      class="flex-1 block w-full focus:ring-indigo-500 focus:border-indigo-500 min-w-0 bg-gray-50 rounded-none rounded-l-md sm:text-sm border-gray-300"
                    >
                    <button @click="copyEmbedUrl" type="button" class="inline-flex items-center px-3 rounded-r-md border border-gray-300 bg-gray-50 text-gray-500 sm:text-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div>
                  <label for="builder_embed_mode" class="block text-sm font-medium text-gray-900">Mode</label>
                  <select
                    id="builder_embed_mode"
                    v-model="draft.embedOptions.mode"
                    class="mt-2 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="view">View</option>
                    <option v-if="supportsEmbedUpdateMode" value="update">Update</option>
                  </select>
                </div>

                <div class="flex items-center justify-between gap-4">
                  <div>
                    <label for="builder_embed_darkmode" class="text-sm font-medium text-gray-900">Dark mode</label>
                    <p class="mt-1 text-sm text-gray-500">Apply the embed dark appearance by default.</p>
                  </div>
                  <div class="flex text-xs">
                    <button
                      id="builder_embed_darkmode"
                      @click="draft.embedOptions.darkMode = !draft.embedOptions.darkMode"
                      type="button"
                      class="mt-1 bg-gray-200 relative inline-flex flex-shrink-0 h-5 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      :class="{'bg-indigo-600': draft.embedOptions.darkMode}"
                      role="switch"
                      :aria-checked="draft.embedOptions.darkMode"
                    >
                      <span class="sr-only">Toggle dark mode</span>
                      <span class="translate-x-0 pointer-events-none relative inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                            :class="{'translate-x-4': draft.embedOptions.darkMode}">
                        <span class="opacity-0 ease-out duration-100 absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                              aria-hidden="true"
                              :class="{'opacity-100 ease-in duration-200': !draft.embedOptions.darkMode}">
                          <svg class="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 12 12">
                            <path d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                        </span>
                        <span class="opacity-0 ease-out duration-100 absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                              :class="{'opacity-100 ease-in duration-200': draft.embedOptions.darkMode}"
                              aria-hidden="true">
                          <svg class="h-3 w-3 text-indigo-600" fill="currentColor" viewBox="0 0 12 12">
                            <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                          </svg>
                        </span>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </main>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import { useToast } from 'vue-toastification';
import { mapGetters } from 'vuex';
import { colors } from '../colors/colors';
import { skins } from '../colors/skins';
import { createBlock, getBlock, updateBlock } from '../models/BlocksModel';
import AutoFitPreview from '@/components/AutoFitPreview.vue';
import Block from '@/components/Block.vue';
import BlockFace from '@/components/BlockFace.vue';
import BlockIcon from '@/components/BlockIcon.vue';
import BlockCountdown from '@/components/BlockCountdown.vue';
import BlockCounter from '@/components/BlockCounter.vue';
import BlockIconPicker from '@/components/BlockIconPicker.vue';
import BlockLink from '@/components/BlockLink.vue';
import BlockProgress from '@/components/BlockProgress.vue';

const BLOCK_COMPONENTS = {
  Block,
  BlockCountdown,
  BlockCounter,
  BlockLink,
  BlockProgress,
};

const BLOCK_TYPES = [
  { value: 'Block', label: 'Time Block' },
  { value: 'BlockCounter', label: 'Counter Block' },
  { value: 'BlockCountdown', label: 'Countdown Block' },
  { value: 'BlockProgress', label: 'Progress Block' },
  { value: 'BlockLink', label: 'Link Block' },
];

function defaultTitle(type) {
  const labels = {
    Block: 'Block Title',
    BlockCounter: 'Block Title',
    BlockCountdown: 'Block Title',
    BlockProgress: 'Block Title',
    BlockLink: 'Block Title',
  };

  return labels[type] || 'Block';
}

function toDatetimeLocalValue(timestamp) {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function fromDatetimeLocalValue(value) {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function defaultEmbedOptions(type = 'Block') {
  return {
    mode: ['Block', 'BlockCounter', 'BlockCountdown'].includes(type) ? 'update' : 'view',
    darkMode: false,
  };
}

function createDraftBlock(type = 'Block') {
  const block = {
    id: 'builder-preview',
    refID: null,
    type,
    displayMode: 'compact',
    title: defaultTitle(type),
    color: 'blue',
    skin: 'neutral',
    size: 2,
    increaseValue: 1,
    faceType: 'default',
    elapsedTime: 0,
    totalTime: 0,
    activeSince: 0,
    activeCountdownSince: 0,
    elements: [],
    counters: [],
    progressStart: Date.now(),
    progressEnd: 0,
    link: '',
    imageLink: '',
    embedId: null,
    embedOptions: null,
  };

  if (type === 'BlockCountdown') {
    block.size = 120;
  }

  return block;
}

function createNewDraft(type = 'Block', enableEmbed = false) {
  const block = createDraftBlock(type);

  if (enableEmbed) {
    block.embedId = uuidv4();
    block.embedOptions = defaultEmbedOptions(type);
  }

  return block;
}

function createDraftFromRecord(record) {
  const recordType = record && record.data && record.data.type ? record.data.type : record && record.type ? record.type : 'Block';
  const base = createDraftBlock(recordType);
  const source = {
    ...(record && record.data ? record.data : {}),
    id: record && record.id ? record.id : base.id,
    refID: record && record.id ? record.id : base.refID,
    type: recordType,
    title: record && record.title ? record.title : base.title,
  };

  return {
    ...base,
    ...source,
    counters: Array.isArray(source.counters) ? JSON.parse(JSON.stringify(source.counters)) : [],
    elements: Array.isArray(source.elements) ? JSON.parse(JSON.stringify(source.elements)) : [],
  };
}

function createDuplicateDraft(record) {
  const duplicated = createDraftFromRecord(record);

  return {
    ...duplicated,
    id: 'builder-preview',
    refID: null,
    embedId: null,
    embedOptions: null,
    activeSince: 0,
    activeCountdownSince: 0,
    elapsedTime: 0,
    totalTime: 0,
    elements: [],
  };
}

export default {
  name: 'BlockBuilder',
  components: {
    AutoFitPreview,
    Block,
    BlockFace,
    BlockIcon,
    BlockCountdown,
    BlockCounter,
    BlockIconPicker,
    BlockLink,
    BlockProgress,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      blockTypes: BLOCK_TYPES,
      colors,
      skins,
      draft: createDraftBlock(),
      currentBlockId: null,
      duplicateSourceId: null,
      isLoading: false,
      isSaving: false,
      iconEdited: null,
      counterNew: {
        title: '',
        increaseValue: 0,
        icon: 'default',
      },
      progressIconEdited: null,
      progressCounterNew: {
        title: '',
        type: 'number',
        day: 0,
        icon: 'default',
      },
      progressCounterTypes: [
        { text: 'Number', value: 'number' },
        { text: 'Date', value: 'date' },
      ],
      faces: [
        { text: 'default', value: 'default' },
        { text: 'cat', value: 'cat' },
        { text: 'bear', value: 'bear' },
        { text: 'owl', value: 'owl' },
        { text: 'humanbear', value: 'humanbear' },
        { text: 'dragon', value: 'dragon' },
        { text: 'hearts', value: 'hearts' },
        { text: 'money', value: 'money' },
        { text: 'argh', value: 'argh' },
        { text: 'pumpkin', value: 'pumpkin' },
      ],
      sizeOptionsHours: [
        { text: '00', value: 0 },
        { text: '01', value: 1 },
        { text: '02', value: 2 },
        { text: '03', value: 3 },
        { text: '04', value: 4 },
        { text: '05', value: 5 },
        { text: '06', value: 6 },
        { text: '07', value: 7 },
        { text: '08', value: 8 },
      ],
      sizeOptionsMinutes: [
        { text: '00', value: 0 },
        { text: '15', value: 0.25 },
        { text: '30', value: 0.5 },
        { text: '45', value: 0.75 },
      ],
      sizeOptionsSeconds: [
        { text: '00', value: 0 },
        { text: '05', value: 5 },
        { text: '10', value: 10 },
        { text: '15', value: 15 },
        { text: '20', value: 20 },
        { text: '25', value: 25 },
        { text: '30', value: 30 },
        { text: '35', value: 35 },
        { text: '40', value: 40 },
        { text: '45', value: 45 },
        { text: '50', value: 50 },
        { text: '55', value: 55 },
      ],
    };
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    isEditing() {
      return !!this.currentBlockId;
    },
    isDuplicating() {
      return !!this.duplicateSourceId;
    },
    isTypeLocked() {
      return this.isEditing || this.isDuplicating;
    },
    pageTitle() {
      if (this.isEditing) return 'Edit Block';
      if (this.isDuplicating) return 'Duplicate Block';
      return 'Build a Block';
    },
    pageDescription() {
      if (this.isEditing) {
        return 'Update the block on the left and review the live preview on the right.';
      }
      if (this.isDuplicating) {
        return 'Review the copied block, adjust its fields, and save it as a new block.';
      }
      return 'Configure a block on the left and see the live preview on the right.';
    },
    saveButtonLabel() {
      return this.isEditing ? 'Save changes' : 'Save block';
    },
    showsDurationFields() {
      return ['Block'].includes(this.draft.type);
    },
    isCountdown() {
      return this.draft.type === 'BlockCountdown';
    },
    isLink() {
      return this.draft.type === 'BlockLink';
    },
    isCounter() {
      return this.draft.type === 'BlockCounter';
    },
    isProgress() {
      return this.draft.type === 'BlockProgress';
    },
    isTimeBlock() {
      return this.draft.type === 'Block';
    },
    showsFacePicker() {
      return !this.isProgress;
    },
    showsSkinPicker() {
      return this.isTimeBlock && this.draft.faceType === 'humanbear';
    },
    showsIncreaseValue() {
      return ['BlockCounter'].includes(this.draft.type);
    },
    supportsEmbedUpdateMode() {
      return ['Block', 'BlockCounter', 'BlockCountdown'].includes(this.draft.type);
    },
    canCopyEmbedUrl() {
      return this.isEditing;
    },
    embedURL() {
      if (!this.draft.embedId) return '';
      return `${window.location.origin}/embed?id=${this.draft.embedId}`;
    },
    draftSizeHours: {
      get() {
        return Math.floor(this.draft.size || 0);
      },
      set(value) {
        const minutes = this.draftSizeMinutes;
        this.draft.size = Number(value) + Number(minutes);
      },
    },
    draftSizeMinutes: {
      get() {
        return Number(((this.draft.size || 0) - Math.floor(this.draft.size || 0)).toFixed(2));
      },
      set(value) {
        this.draft.size = Number(this.draftSizeHours) + Number(value);
      },
    },
    progressStartInput: {
      get() {
        return toDatetimeLocalValue(this.draft.progressStart);
      },
      set(value) {
        this.draft.progressStart = fromDatetimeLocalValue(value);
      },
    },
    progressEndInput: {
      get() {
        return toDatetimeLocalValue(this.draft.progressEnd);
      },
      set(value) {
        this.draft.progressEnd = fromDatetimeLocalValue(value);
      },
    },
    countdownSizeMinutes: {
      get() {
        return Math.floor((Number(this.draft.size) || 0) / 60);
      },
      set(value) {
        this.draft.size = (Number(value) * 60) + this.countdownSizeSeconds;
      },
    },
    countdownSizeSeconds: {
      get() {
        const totalSeconds = Number(this.draft.size) || 0;
        return totalSeconds - (Math.floor(totalSeconds / 60) * 60);
      },
      set(value) {
        this.draft.size = (this.countdownSizeMinutes * 60) + Number(value);
      },
    },
    previewComponent() {
      return BLOCK_COMPONENTS[this.draft.type] || Block;
    },
    previewProps() {
      const draft = this.normalizedDraft;

      return {
        id: draft.id,
        refID: draft.refID,
        title: draft.title,
        type: draft.type,
        color: draft.color,
        skin: draft.skin,
        size: draft.size,
        faceType: draft.faceType,
        increaseValue: draft.increaseValue,
        elapsedTime: draft.elapsedTime,
        totalTime: draft.totalTime,
        activeSince: draft.type === 'Block' ? 1 : draft.activeSince,
        activeCountdownSince: draft.activeCountdownSince,
        elements: draft.elements,
        counters: draft.counters,
        progressStart: draft.progressStart,
        progressEnd: draft.progressEnd,
        link: draft.link,
        imageLink: draft.imageLink,
        embedId: draft.embedId,
        embedOptions: draft.embedOptions,
        boardMode: draft.type === 'Block' ? draft.displayMode : undefined,
        preview: true,
        buildMode: false,
        embed: false,
        embedPreview: false,
        isLoading: false,
        hasError: false,
        isNew: false,
        blockTick: Date.now(),
      };
    },
    normalizedDraft() {
      const title = this.draft.title && this.draft.title.trim() ? this.draft.title.trim() : defaultTitle(this.draft.type);
      const block = {
        ...this.draft,
        title,
        displayMode: this.draft.type === 'Block' && this.draft.displayMode === 'default' ? 'default' : 'compact',
        skin: this.draft.skin || 'neutral',
        size: Number(this.draft.size) || 0,
        increaseValue: Number(this.draft.increaseValue) || 1,
        progressEnd: Number(this.draft.progressEnd) || 0,
      };

      if (block.type !== 'Block') {
        delete block.displayMode;
      }

      if (block.embedId) {
        const currentEmbedOptions = block.embedOptions || defaultEmbedOptions(block.type);
        block.embedOptions = {
          mode: this.supportsEmbedUpdateMode && currentEmbedOptions.mode === 'update' ? 'update' : 'view',
          darkMode: !!currentEmbedOptions.darkMode,
        };
      } else {
        block.embedOptions = null;
      }

      if (block.type === 'BlockCounter') {
        block.counters = block.counters
          .filter(counter => counter && (counter.title || counter.icon))
          .map(counter => ({
            id: counter.id || uuidv4().substring(0, 7),
            title: counter.title || '',
            increaseValue: Number(counter.increaseValue) || 0,
            icon: counter.icon || 'default',
          }));
      }

      if (block.type === 'BlockProgress') {
        block.counters = (block.counters || [])
          .filter(counter => counter && (counter.title || counter.icon))
          .map(counter => ({
            id: counter.id || uuidv4().substring(0, 7),
            title: counter.title || '',
            type: counter.type || 'number',
            day: counter.day ?? 0,
            icon: counter.icon || 'default',
          }));
      }

      return block;
    },
  },
  watch: {
    '$route.fullPath': {
      immediate: true,
      handler() {
        this.initializeBuilder();
      },
    },
  },
  methods: {
    async initializeBuilder() {
      const blockId = this.$route && this.$route.query ? this.$route.query.id : null;
      const duplicateId = this.$route && this.$route.query ? this.$route.query.duplicateId : null;

      this.iconEdited = null;
      this.progressIconEdited = null;
      this.currentBlockId = null;
      this.duplicateSourceId = null;

      if (!blockId && !duplicateId) {
        this.draft = createNewDraft('Block', !!this.currentUser);
        return;
      }

      this.isLoading = true;

      try {
        const sourceId = blockId || duplicateId;
        const record = await getBlock(sourceId);

        if (!record) {
          this.toast.error('Block not found.');
          this.$router.replace({ name: 'Blocks' });
          return;
        }

        if (blockId) {
          this.currentBlockId = record.id;
          this.draft = createDraftFromRecord(record);
        } else {
          this.duplicateSourceId = record.id;
          this.draft = createDuplicateDraft(record);
        }
      } catch (error) {
        this.toast.error('Unable to load the block.');
        this.$router.replace({ name: 'Blocks' });
      } finally {
        this.isLoading = false;
      }
    },
    handleTypeChange() {
      const current = this.draft;
      const next = createDraftBlock(current.type);
      next.title = current.title && current.title.trim() ? current.title : next.title;
      next.displayMode = current.displayMode === 'default' ? 'default' : 'compact';
      next.color = current.color;
      next.skin = current.skin || next.skin;
      next.faceType = current.faceType || next.faceType;
      if (current.type === 'BlockCounter') {
        next.increaseValue = Number(current.increaseValue) || next.increaseValue;
        next.size = Number(current.size) || next.size;
        next.counters = JSON.parse(JSON.stringify(current.counters || next.counters));
      }
      if (current.type === 'BlockProgress') {
        next.progressStart = Number(current.progressStart) || next.progressStart;
        next.progressEnd = Number(current.progressEnd) || 0;
        next.counters = JSON.parse(JSON.stringify(current.counters || []));
      }
      if (current.embedId) {
        const nextEmbedOptions = current.embedOptions || defaultEmbedOptions(current.type);
        next.embedId = current.embedId;
        next.embedOptions = {
          mode: ['Block', 'BlockCounter', 'BlockCountdown'].includes(current.type) && nextEmbedOptions.mode === 'update' ? 'update' : 'view',
          darkMode: !!nextEmbedOptions.darkMode,
        };
      }
      this.draft = next;
      this.iconEdited = null;
      this.progressIconEdited = null;
      this.counterNew = {
        title: '',
        increaseValue: 0,
        icon: 'default',
      };
      this.progressCounterNew = {
        title: '',
        type: 'number',
        day: 0,
        icon: 'default',
      };
    },
    updateFace(face) {
      this.draft.faceType = face;
    },
    counterChangeIconOpen(index) {
      this.iconEdited = this.iconEdited === index ? null : index;
    },
    counterChangeIcon(icon, index) {
      if (index === 'new') {
        this.counterNew.icon = icon;
      } else if (typeof this.draft.counters[index] !== 'undefined') {
        this.draft.counters[index].icon = icon;
      }
      this.iconEdited = null;
    },
    counterDelete(index) {
      this.draft.counters.splice(index, 1);
    },
    counterAdd() {
      this.draft.counters.push({
        id: uuidv4().substring(0, 7),
        title: this.counterNew.title.trim(),
        increaseValue: Number(this.counterNew.increaseValue) || 0,
        icon: this.counterNew.icon || 'default',
      });
      this.counterNew = {
        title: '',
        increaseValue: 0,
        icon: 'default',
      };
      this.iconEdited = null;
    },
    progressChangeIconOpen(index) {
      this.progressIconEdited = this.progressIconEdited === index ? null : index;
    },
    progressChangeIcon(icon, index) {
      if (index === 'new') {
        this.progressCounterNew.icon = icon;
      } else if (typeof this.draft.counters[index] !== 'undefined') {
        this.draft.counters[index].icon = icon;
      }
      this.progressIconEdited = null;
    },
    progressDelete(index) {
      this.draft.counters.splice(index, 1);
    },
    progressAdd() {
      this.draft.counters.push({
        id: uuidv4().substring(0, 7),
        title: this.progressCounterNew.title.trim(),
        type: this.progressCounterNew.type || 'number',
        day: this.progressCounterNew.day,
        icon: this.progressCounterNew.icon || 'default',
      });
      this.progressCounterNew = {
        title: '',
        type: 'number',
        day: 0,
        icon: 'default',
      };
      this.progressIconEdited = null;
    },
    embedToggle() {
      if (this.draft.embedId) {
        this.draft.embedId = null;
        this.draft.embedOptions = null;
        return;
      }

      this.draft.embedId = uuidv4();
      this.draft.embedOptions = defaultEmbedOptions(this.draft.type);
    },
    async copyEmbedUrl() {
      if (!this.embedURL) return;

      try {
        await navigator.clipboard.writeText(this.embedURL);
        this.toast.success('Embed URL copied to clipboard!');
      } catch (error) {
        this.toast.error('Failed to copy embed URL.');
      }
    },
    async saveBlock() {
      if (this.isSaving || this.isLoading) return;

      this.isSaving = true;

      try {
        let block;
        let blockData;

        if (this.isEditing) {
          block = {
            ...this.normalizedDraft,
            id: this.currentBlockId,
            refID: this.currentBlockId,
          };
          blockData = {
            type: block.type,
            title: block.title,
            data: block,
          };

          await updateBlock(this.currentBlockId, blockData);
          this.toast.success('Block updated.');
        } else {
          const savedId = uuidv4();
          block = {
            ...this.normalizedDraft,
            id: savedId,
            refID: savedId,
          };
          blockData = {
            id: savedId,
            type: block.type,
            title: block.title,
            data: block,
          };

          await createBlock(blockData);
          this.$store.commit('blockAdd', block);
          if (typeof window.EngageKit !== 'undefined' && window.EngageKit && typeof window.EngageKit.track === 'function') {
            window.EngageKit.track('Block ' + block.type + ' Created');
          }
          this.toast.success('Block created.');
        }

        this.$router.push({ name: 'Blocks' });
      } catch (error) {
        this.toast.error(this.isEditing ? 'Unable to update the block.' : 'Unable to create the block.');
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.kairo-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-width: 2px;
  border-bottom-width: 3px;
  border-style: solid;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem 0.75rem;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.25rem;
  outline: none;
}

.kairo-button:focus {
  box-shadow: var(--kairo-inset-shadow), 0 0 0 2px var(--kairo-ring-color);
}

.kairo-button:hover {
  background-color: var(--kairo-bg-hover);
}

.kairo-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.kairo-button-small {
  padding: 0.375rem 0.75rem 0.625rem;
  font-size: 0.75rem;
  line-height: 1rem;
}

.kairo-button-green {
  color: #059669;
  background-color: #bbf7d0;
  border-color: #16a34a;
  --kairo-bg-hover: #dcfce7;
  --kairo-ring-color: #f0fdf4;
  --kairo-inset-shadow: inset 0px -6px 0px #34d399, inset 0 -7px 0 0 #ecfdf5, inset 0px 1px 0px 1px #ecfdf5;
  box-shadow: var(--kairo-inset-shadow), 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.kairo-button-blue {
  color: #1e3a8a;
  background-color: #dbeafe;
  border-color: #1e3a8a;
  --kairo-bg-hover: #eff6ff;
  --kairo-ring-color: #dbeafe;
  --kairo-inset-shadow: inset 0px -6px 0px #93c5fd, inset 0 -7px 0 0 #eff6ff, inset 0px 1px 0px 1px #eff6ff;
  box-shadow: var(--kairo-inset-shadow), 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.kairo-button-yellow {
  color: #a16207;
  background-color: #fde68a;
  border-color: #ca8a04;
  --kairo-bg-hover: #fef3c7;
  --kairo-ring-color: #fffbeb;
  --kairo-inset-shadow: inset 0px -6px 0px #fcd34d, inset 0 -7px 0 0 #fefce8, inset 0px 1px 0px 1px #fefce8;
  box-shadow: var(--kairo-inset-shadow), 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.kairo-button-gray {
  color: #4b5563;
  background-color: #e5e7eb;
  border-color: #4b5563;
  --kairo-bg-hover: #f3f4f6;
  --kairo-ring-color: #f9fafb;
  --kairo-inset-shadow: inset 0px -6px 0px #d1d5db, inset 0 -7px 0 0 #f9fafb, inset 0px 1px 0px 1px #f9fafb;
  box-shadow: var(--kairo-inset-shadow), 0 1px 2px 0 rgb(0 0 0 / 0.05);
}
</style>